import dayjs, { type Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(customParseFormat)
dayjs.extend(utc)
dayjs.extend(timezone)

export const ADMIN_DATETIME_TZ = 'America/Chicago'

export type Meridiem = 'am' | 'pm'

export interface DateTimeInputParts {
  date: string
  time: string
  ampm: Meridiem
}

export interface AdminDateTimeSelectParts {
  date: string
  hour: string
  minute: string
  meridiem: Meridiem
}

export const ADMIN_DATETIME_HOUR_OPTIONS = Array.from({ length: 12 }, (_, index) => {
  const hour = index === 0 ? 12 : index
  return String(hour).padStart(2, '0')
})

export const ADMIN_DATETIME_MINUTE_OPTIONS = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'))

const EMPTY_SELECT_PARTS: AdminDateTimeSelectParts = {
  date: '',
  hour: '12',
  minute: '00',
  meridiem: 'pm',
}

export function normalizeMeridiem(value: string): Meridiem {
  return value.toLowerCase().startsWith('a') ? 'am' : 'pm'
}

export function parseAdminDateTime(value: string | null | undefined): Dayjs | null {
  if (!value) {
    return null
  }

  const parsed = dayjs(value).tz(ADMIN_DATETIME_TZ)
  return parsed.isValid() ? parsed : null
}

export function formatAdminDateTimeParts(value: string | null | undefined): DateTimeInputParts {
  const parsed = parseAdminDateTime(value)
  if (!parsed) {
    return { date: '', time: '', ampm: 'pm' }
  }

  return {
    date: parsed.format('MM/DD/YYYY'),
    time: parsed.format('hh:mm'),
    ampm: normalizeMeridiem(parsed.format('a')),
  }
}

export function formatAdminDateTimeSelectParts(value: string | null | undefined): AdminDateTimeSelectParts {
  const parsed = parseAdminDateTime(value)
  if (!parsed) {
    return { ...EMPTY_SELECT_PARTS }
  }

  return {
    date: parsed.format('YYYY-MM-DD'),
    hour: parsed.format('hh'),
    minute: parsed.format('mm'),
    meridiem: normalizeMeridiem(parsed.format('a')),
  }
}

export function formatAdminDateTimePreview(value: string | null | undefined): string | null {
  const parsed = parseAdminDateTime(value)
  if (!parsed) {
    return null
  }

  return parsed.format('ddd, MMM D, YYYY · h:mm A [CT]')
}

export function isCompleteDate(value: string): boolean {
  return /^\d{2}\/\d{2}\/\d{4}$/.test(value.replace(/_/g, '')) && value.replace(/_/g, '').length === 10
}

export function isCompleteTime(value: string): boolean {
  return /^\d{2}:\d{2}$/.test(value.replace(/_/g, '')) && value.replace(/_/g, '').length === 5
}

export function clampTimeInput(value: string): string {
  const [hourPart, minutePart] = value.split(':')
  let hour = hourPart ?? ''
  let minute = minutePart ?? ''

  if (hour && Number(hour.replace(/_/g, '0')) > 12) {
    hour = '12'
  }

  if (minute && Number(minute.replace(/_/g, '0')) > 59) {
    minute = '59'
  }

  if (!minutePart) {
    return hour
  }

  return `${hour}:${minute}`
}

export function buildAdminDateTimeIso(date: string, time: string, meridiem: Meridiem): string | null {
  if (!isCompleteDate(date) || !isCompleteTime(time)) {
    return null
  }

  const [hourPart, minutePart] = time.split(':')
  let hour = Number(hourPart)
  const minute = Number(minutePart)

  if (Number.isNaN(hour) || Number.isNaN(minute) || hour < 1 || hour > 12 || minute < 0 || minute > 59) {
    return null
  }

  if (meridiem === 'pm' && hour < 12) {
    hour += 12
  }
  if (meridiem === 'am' && hour === 12) {
    hour = 0
  }

  const combined = `${date} ${hour}:${String(minute).padStart(2, '0')}`
  const parsed = dayjs.tz(combined, 'MM/DD/YYYY H:mm', ADMIN_DATETIME_TZ)

  return parsed.isValid() ? parsed.toISOString() : null
}

export function buildAdminDateTimeIsoFromSelectParts(parts: AdminDateTimeSelectParts): string | null {
  if (!parts.date) {
    return null
  }

  const displayDate = dayjs.tz(parts.date, 'YYYY-MM-DD', ADMIN_DATETIME_TZ).format('MM/DD/YYYY')
  return buildAdminDateTimeIso(displayDate, `${parts.hour}:${parts.minute}`, parts.meridiem)
}

/** An Eventbrite start as the event's own local time ("Fri, Nov 6, 7:30 PM CST"), not the browser's. */
export const formatEventbriteStart = (iso: string | null | undefined, timeZone?: string | null): string => {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const format = (zone: string) =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(date)
  try {
    return format(timeZone || ADMIN_DATETIME_TZ)
  } catch {
    return format(ADMIN_DATETIME_TZ)
  }
}
